import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../../services/api";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

type ProsthoCase = {
    id: number;
    patient_name: string;
    patient_id: string;
    patient_phone?: string;
    patient_age?: number;
    patient_gender?: string;
    teeth: string | null;
    stage: string;
    status: string;
    lab_name?: string;
    notes?: string;
    created_at: string;
    origin_procedure?: string;
};

export default function CrownProcessWidget() {
    const { showToast } = useToast();
    const [cases, setCases] = useState<ProsthoCase[]>([]);
    const [loading, setLoading] = useState(true);

    const [showLabModal, setShowLabModal] = useState<number | null>(null);
    const [labNameInput, setLabNameInput] = useState("");

    const loadData = async () => {
        try {
            const data = await apiGet("/api/treatments/prostho/cases?type=RPD&status=Active&stage=Crown%20Process");
            if (Array.isArray(data)) {
                setCases(data);
            }
        } catch (e) {
            console.error("Failed to load RPD cases", e);
            showToast({ message: "Failed to load Crown Process cases", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleSendClick = (caseId: number) => {
        setLabNameInput("");
        setShowLabModal(caseId);
    };

    const confirmSendToLab = async () => {
        if (!showLabModal || !labNameInput.trim()) return;
        try {
            await apiPost(`/api/treatments/prostho/cases/${showLabModal}/update`, {
                stage: "Lab Work",
                lab_name: labNameInput.trim(),
                status: "In Progress"
            });
            showToast({ message: "Case sent to lab successfully", type: "success" });
            setShowLabModal(null);
            loadData();
        } catch (e) {
            showToast({ message: "Failed to update case", type: "error" });
        }
    };

    return (
        <div className="mod-card" style={{ minHeight: '200px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>👑 Crown Process (RPD)</h3>
                <span style={{ fontSize: '0.8rem', background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>{cases.length} Active</span>
            </div>
            <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>Loading...</div>
                ) : cases.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                        No active crown cases pending.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {cases.map(c => (
                            <div key={c.id} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px', background: '#fff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                    <strong style={{ color: '#0f172a', display: 'block' }}>{c.patient_name}</strong>
                                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600 }}>{c.stage}</span>
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span>ID: {c.patient_id} • 📞 {c.patient_phone || "—"}</span>
                                    <span>Ref: {c.teeth || "Full Arch"} • {new Date(c.created_at).toLocaleDateString()}</span>
                                </div>
                                {(c.stage === "Crown Process" || c.stage === "Pending") && (
                                    <div style={{ marginTop: '12px' }}>
                                        <Button variant="outline" size="sm" fullWidth onClick={() => handleSendClick(c.id)}>
                                            Send to Lab
                                        </Button>
                                    </div>
                                )}
                                {c.stage === "Lab Work" && (
                                    <div style={{ fontSize: '0.8rem', color: '#059669', marginTop: '8px', fontWeight: 500 }}>
                                        Lab: {c.lab_name}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showLabModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowLabModal(null)}>
                    <div style={{ width: '400px', background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', color: '#0f172a' }}>Enter Lab Name</h3>
                        <input
                            type="text"
                            autoFocus
                            value={labNameInput}
                            onChange={e => setLabNameInput(e.target.value)}
                            placeholder="e.g. Elite Dental Lab"
                            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', marginBottom: '24px', fontSize: '1rem', outline: 'none' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <Button variant="ghost" onClick={() => setShowLabModal(null)}>Cancel</Button>
                            <Button variant="primary" onClick={confirmSendToLab} disabled={!labNameInput.trim()}>Confirm Send</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
